import React, {useEffect, useState} from 'react';
import {RouteComponentProps, Link} from 'react-router-dom';
import {Box, Typography, Breadcrumbs, Tabs, Tab} from '@material-ui/core';
import {connect} from 'react-redux';
import axios from 'axios';

import Lottie from 'react-lottie';
import IntelligenceLoader from '../../../../../assets/images/IntelligenceLoader.json';

import {
  ISupplierUserProfile,
  IUser,
  ICOA,
  IcoaTransactions,
} from 'constants/interfaces';
import {apiGetUser} from 'constants/Api';

import Overview from './Overview/Overview';
import ChartOfAccounts from './ChartOfAccounts/ChartOfAccounts';
import Reports from './Reports/Reports';

import './AIBookkeeper.scss';

interface MatchParams {
  customerUserId: string;
}

interface AIBookkeeperProps extends RouteComponentProps<MatchParams> {
  user: ISupplierUserProfile;
}

function AIBookkeeper(props: AIBookkeeperProps) {
  const {customerUserId} = props.match.params;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [customerData, setCustomerData] = useState<IUser | null>(null);

  // Centralized account/transaction data
  const [allAccounts, setAllAccounts] = useState<ICOA[]>([]);
  const [allTransactions, setAllTransactions] = useState<IcoaTransactions[]>(
    [],
  );

  // Tab handling
  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setIsLoading(true);
    apiGetUser(customerUserId)
      .then(async (fetchedUser) => {
        setCustomerData(fetchedUser);

        // 1) Fetch user accounts
        const accountsRes = await axios.get(
          `http://khuladev.co.za:4010/accounts/user/${fetchedUser.id}`,
        );
        const userAccounts = accountsRes.data as ICOA[];
        setAllAccounts(userAccounts);

        // 2) For each account, fetch transactions
        const promises = userAccounts.map((acct) =>
          axios
            .get(
              `http://khuladev.co.za:4010/accounts/transactions/${acct._id}/${fetchedUser.id}`,
            )
            .then((res) => res.data as IcoaTransactions[]),
        );
        const results = await Promise.all(promises);

        // Flatten all transactions into a single array
        const combinedTxns = results.flat();
        setAllTransactions(combinedTxns);
      })
      .catch((err) => {
        console.error('Error fetching user or accounts/transactions', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [customerUserId]);

  const customerFullName = customerData?.farmer
    ? `${customerData.farmer.name} ${customerData.farmer.surname}`
    : customerData
    ? `${customerData.name} ${customerData.surname}`
    : 'Customer';

  // Lottie loader configuration
  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: IntelligenceLoader,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <Box className="ai-bookkeeper-container">
      {/* Breadcrumb */}
      <Breadcrumbs aria-label="breadcrumb" className="breadcrumb-row">
        <Link
          to="/customers"
          style={{textDecoration: 'none', color: '#267373'}}
        >
          Farmers
        </Link>
        <Link
          to={`/customers/dashboard/${customerUserId}`}
          style={{textDecoration: 'none', color: '#267373'}}
        >
          {customerFullName}
        </Link>
        <Typography color="textPrimary" style={{fontWeight: 700}}>
          AI Bookkeeper
        </Typography>
      </Breadcrumbs>

      {/* Always render the main container (removed the top skeleton loader) */}
      <Box className="ai-bookkeeper-content">
        {/* Tabs */}
        <Box className="tabs-section">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            className="ai-bookkeeper-tabs"
          >
            <Tab label="Overview" />
            <Tab label="Chart of Accounts" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box className="tab-content">
          {tabValue === 0 && (
            <Overview
              user={props.user}
              customerData={customerData}
              accounts={allAccounts}
              transactions={allTransactions}
            />
          )}

          {tabValue === 1 &&
            (isLoading ? (
              // Show the Lottie loader in the transactions section
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="300px"
              >
                <Lottie options={lottieOptions} height={200} width={200} />
              </Box>
            ) : (
              <ChartOfAccounts
                user={props.user}
                customerData={customerData}
                accounts={allAccounts}
                allTransactions={allTransactions}
              />
            ))}

          {tabValue === 2 && <Reports />}
        </Box>
      </Box>
    </Box>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user as ISupplierUserProfile,
});

export default connect(mapStateToProps)(AIBookkeeper);
